import { createSlice, PayloadAction } from "@reduxjs/toolkit";

const STORAGE_KEY_PREFIX = "appliedGridFilters:";
const LEGACY_STORAGE_KEY = "appliedGridFilters";

export interface AppliedGridFilterState {
  tableFilters: Record<string, any>;
  searchFilters: Record<string, any>;
}

const emptyState: AppliedGridFilterState = {
  tableFilters: {},
  searchFilters: {},
};

const canUseSessionStorage = () =>
  typeof window !== "undefined" && !!window.sessionStorage;

const getModuleStorageKey = (module: string) =>
  `${STORAGE_KEY_PREFIX}${module}`;

const readStoredModuleFilters = (module: string) => {
  try {
    if (!canUseSessionStorage()) return null;

    const storedValue = window.sessionStorage.getItem(
      getModuleStorageKey(module),
    );
    if (!storedValue) return null;

    const parsedValue = JSON.parse(storedValue);
    return {
      tableFilters: parsedValue?.tableFilters || {},
      searchFilters: parsedValue?.searchFilters || {},
    };
  } catch {
    return null;
  }
};

const readStoredFilters = (): AppliedGridFilterState => {
  const state: AppliedGridFilterState = {
    tableFilters: {},
    searchFilters: {},
  };

  try {
    if (!canUseSessionStorage()) return emptyState;

    for (let index = 0; index < window.sessionStorage.length; index += 1) {
      const key = window.sessionStorage.key(index);
      if (!key?.startsWith(STORAGE_KEY_PREFIX)) continue;

      const module = key.replace(STORAGE_KEY_PREFIX, "");
      const moduleFilters = readStoredModuleFilters(module);
      if (!moduleFilters) continue;

      state.tableFilters[module] = moduleFilters.tableFilters;
      state.searchFilters[module] = moduleFilters.searchFilters;
    }

    return state;
  } catch {
    return emptyState;
  }
};

const writeStoredModuleFilters = (
  module: string,
  moduleFilters: { tableFilters?: any; searchFilters?: any },
) => {
  try {
    if (!canUseSessionStorage()) return;

    window.sessionStorage.setItem(
      getModuleStorageKey(module),
      JSON.stringify({
        tableFilters: moduleFilters.tableFilters || {},
        searchFilters: moduleFilters.searchFilters || {},
      }),
    );
  } catch {
    // Session storage can be unavailable in private modes or tests.
  }
};

const removeStoredModuleFilters = (module: string) => {
  try {
    if (!canUseSessionStorage()) return;
    window.sessionStorage.removeItem(getModuleStorageKey(module));
  } catch {
    // Session storage can be unavailable in private modes or tests.
  }
};

const removeAllStoredFilters = () => {
  try {
    if (!canUseSessionStorage()) return;

    const filterKeys: string[] = [];
    for (let index = 0; index < window.sessionStorage.length; index += 1) {
      const key = window.sessionStorage.key(index);
      if (key?.startsWith(STORAGE_KEY_PREFIX) || key === LEGACY_STORAGE_KEY) {
        filterKeys.push(key);
      }
    }

    filterKeys.forEach((key) => window.sessionStorage.removeItem(key));
  } catch {
    // Session storage can be unavailable in private modes or tests.
  }
};

const initialState: AppliedGridFilterState = readStoredFilters();

const appliedFiltersSlice = createSlice({
  name: "appliedFilters",
  initialState,
  reducers: {
    setTableFilters(
      state,
      action: PayloadAction<{ module: string; filters: any }>,
    ) {
      state.tableFilters[action.payload.module] = action.payload.filters;
      writeStoredModuleFilters(action.payload.module, {
        tableFilters: state.tableFilters[action.payload.module],
        searchFilters: state.searchFilters[action.payload.module],
      });
    },
    setSearchFilters(
      state,
      action: PayloadAction<{ module: string; filters: any }>,
    ) {
      state.searchFilters[action.payload.module] = action.payload.filters;
      writeStoredModuleFilters(action.payload.module, {
        tableFilters: state.tableFilters[action.payload.module],
        searchFilters: state.searchFilters[action.payload.module],
      });
    },
    clearAppliedFiltersForModule(state, action: PayloadAction<string>) {
      delete state.tableFilters[action.payload];
      delete state.searchFilters[action.payload];
      removeStoredModuleFilters(action.payload);
    },
    clearAllAppliedFilters() {
      removeAllStoredFilters();
      return emptyState;
    },
  },
});

export const {
  setTableFilters,
  setSearchFilters,
  clearAppliedFiltersForModule,
  clearAllAppliedFilters,
} = appliedFiltersSlice.actions;

export default appliedFiltersSlice.reducer;

export const selectAppliedTableFilters = (state: any, module: string) =>
  state.appliedFilters.tableFilters[module];

export const selectAppliedSearchFilters = (state: any, module: string) =>
  state.appliedFilters.searchFilters[module];
